import { Check, Shield, X } from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { AccessArea } from "../../constants/accessAreas.constants";
import type { AccessRole } from "../../types/access.types";
import { gatherStatements, resolveCell } from "../../utils/denyFirst";
import {
  areaLevelTargets,
  cellKey,
  grantingRoleNames,
  indexByCell,
  roleCell,
  type AreaLevel,
  type PermissionCell,
} from "../../utils/permissionCells";
import { PermissionBoard, type BoardCellState } from "./PermissionBoard";

export interface RolePickerListProps {
  roles: AccessRole[];
  /** Currently picked role ids. The parent owns the set — this component only reports changes. */
  selected: string[];
  onChange: (roleIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Role picker for an invitation: the base roles as the area board (Ver / Editar / Eliminar per module), and
 * the roles that combine several permissions as a short list above it.
 *
 * Selection is the parent's state, not this component's: the invite drawer submits it with the invitation, so
 * a pick here must not imply a save. That is the difference from `AssignRolesDrawer`, which auto-saves every
 * toggle against an existing principal and therefore cannot be reused for a person who does not exist yet.
 * Filtering is local: the whole catalog is already in memory, and `GET /internal/roles` accepts no search.
 */
export function RolePickerList({ roles, selected, onChange, disabled }: RolePickerListProps) {
  const { t } = useTranslations("settings");
  const [term, setTerm] = useState("");
  const deferredTerm = useDeferredValue(term);
  const needle = deferredTerm.trim().toLowerCase();

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const roleIndex = useMemo(() => indexByCell(roles, roleCell), [roles]);
  const statements = useMemo(() => gatherStatements(selected, roles), [selected, roles]);

  const combinedRoles = useMemo(
    () =>
      roleIndex.rest.filter(
        (role) =>
          !needle ||
          [role.name, role.slug, role.description ?? ""].some((field) =>
            field.toLowerCase().includes(needle),
          ),
      ),
    [roleIndex, needle],
  );

  const backingRoles = (cell: PermissionCell) =>
    roleIndex.byCell.get(cellKey(cell.moduleId, cell.atom)) ?? [];

  const cellState = (cell: PermissionCell): BoardCellState => {
    const backing = backingRoles(cell);
    if (backing.some((role) => selectedSet.has(role.id))) return "on";
    const effective = resolveCell(statements, cell.moduleId, cell.atom).effect;
    if (effective === "allow") return "covered";
    if (effective === "deny") return "denied";
    return backing.length > 0 ? "off" : "unavailable";
  };

  const coveredHint = (cell: PermissionCell) => {
    const names = grantingRoleNames(statements, cell);
    if (names.length === 0) return undefined;
    const section = t("access_drawer_combined_roles");
    return names.length === 1
      ? t("access_cell_covered_by_role_one", { roles: names, section })
      : t("access_cell_covered_by_role", { roles: names, section });
  };

  const setCell = (next: Set<string>, cell: PermissionCell, on: boolean) => {
    const backing = backingRoles(cell);
    if (backing.length === 0) return;
    if (on) {
      if (!backing.some((role) => next.has(role.id))) next.add(backing[0].id);
    } else {
      for (const role of backing) next.delete(role.id);
    }
  };

  const toggleCell = (cell: PermissionCell) => {
    const next = new Set(selectedSet);
    setCell(next, cell, cellState(cell) !== "on");
    onChange([...next]);
  };

  const applyLevel = (area: AccessArea, level: AreaLevel) => {
    const next = new Set(selectedSet);
    for (const target of areaLevelTargets(area, level)) {
      const state = cellState(target);
      if (state === "on" || state === "off") setCell(next, target, target.on);
    }
    onChange([...next]);
  };

  const toggleRole = (roleId: string) => {
    const next = new Set(selectedSet);
    if (next.has(roleId)) next.delete(roleId);
    else next.add(roleId);
    onChange([...next]);
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <ColocaSearchInput
            value={term}
            onValueChange={setTerm}
            placeholder={t("access_board_search_placeholder")}
          />
        </div>
        {selected.length > 0 && (
          <ColocaButton
            type="button"
            variant="ghost"
            size="xs"
            icon={X}
            disabled={disabled}
            onClick={() => onChange([])}
          >
            {t("access_invite_roles_clear")}
          </ColocaButton>
        )}
      </div>

      {combinedRoles.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11.5px] font-medium text-foreground">
            {t("access_drawer_combined_roles")}
          </p>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {combinedRoles.map((role) => {
              const isPicked = selectedSet.has(role.id);
              return (
                <li key={role.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isPicked}
                    disabled={disabled}
                    onClick={() => toggleRole(role.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                      "disabled:cursor-not-allowed disabled:opacity-60",
                      isPicked
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:bg-foreground/5",
                    )}
                  >
                    <span
                      className={cn(
                        "grid h-[18px] w-[18px] shrink-0 place-items-center rounded border",
                        isPicked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card",
                      )}
                    >
                      {isPicked && <Check size={12} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-medium text-foreground">
                        {role.name}
                      </span>
                      {role.description && (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {role.description}
                        </span>
                      )}
                    </span>
                    {role.organization_id == null && (
                      <ColocaBadge variant="secondary" className="shrink-0 text-[10px]">
                        {t("access_invite_roles_global")}
                      </ColocaBadge>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <PermissionBoard
        query={deferredTerm}
        cellState={cellState}
        coveredHint={coveredHint}
        coveredLegend={t("access_board_covered_legend", { section: t("access_drawer_combined_roles") })}
        onToggle={disabled ? undefined : toggleCell}
        onApplyLevel={disabled ? undefined : applyLevel}
      />

      <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <Shield size={12} className="shrink-0" />
        {selected.length === 0
          ? t("access_invite_roles_none_selected")
          : t("access_invite_roles_count", { count: selected.length })}
      </p>
    </div>
  );
}
